// ForwardRefEditor.tsx
import React, { forwardRef, useContext, useEffect, useState, Suspense, useMemo } from "react";
import { type MDXEditorMethods, type MDXEditorProps } from "@mdxeditor/editor";

import { appState } from "@/app/appState";
import { observer } from "mobx-react-lite";

// Lazy load the editor component
const InitializedMDXEditor = React.lazy(() => import("./InitializedMDXEditor"));

const EditorFallback = observer(function EditorFallback() {
    return <div>{appState.t("common.loadingEditor")}</div>;
});

const Editor = (props: any) => (
    <Suspense fallback={<EditorFallback />}>
        <InitializedMDXEditor {...props} />
    </Suspense>
);

const detectMarkdownOptions = (markdown: string) => {
    const options: any = {
        rule: '-',
        bullet: '-',
        listItemIndent: 'one',
    };

    // Detect rule style
    if (/^---$/m.test(markdown)) {
        options.rule = '-';
    } else if (/^\*\*\*$/m.test(markdown)) {
        options.rule = '*';
    } else if (/^___$/m.test(markdown)) {
        options.rule = '_';
    }

    // Detect bullet style
    if (/^(\s*)-\s/m.test(markdown)) {
        options.bullet = '-';
    } else if (/^(\s*)\*\s/m.test(markdown)) {
        options.bullet = '*';
    } else if (/^(\s*)\+\s/m.test(markdown)) {
        options.bullet = '+';
    }

    // Detect break style (trailing spaces)
    if (/  $/m.test(markdown)) {
        options.handlers = {
            break: () => '  \n'
        };
    }

    return options;
};

// This is what is imported by other components. Pre-initialized with plugins, and ready
// to accept other props, including a ref.
export const ForwardRefEditor = observer(forwardRef<MDXEditorMethods, MDXEditorProps>(
    (props, ref) => {
        const markdown = props.markdown || "";
        const detectedOptions = useMemo(() => detectMarkdownOptions(markdown), [markdown]);

        return (
            <Editor {...props} toMarkdownOptions={{ ...detectedOptions, ...props.toMarkdownOptions }} className="!z-20 h-full relative !w-full !max-w-full text-black" contentEditableClassName={`prose dark:prose-invert ${props.contentEditableClassName}`} editorRef={ref} />
        );
    }
));

// TS complains without the following line
ForwardRefEditor.displayName = "ForwardRefEditor";
